import React, { useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../../context/WalletContext';
import { useContracts } from '../../context/ContractContext';
import StatCard from '../common/StatCard';
import InlineLoader from '../common/InlineLoader';
import { fromBN, shortAddr } from '../../utils/format';

export default function DashboardStats({ refreshFlag }) {
  const { account } = useWallet();
  const { main, views } = useContracts();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!account) { setLoading(false); return; }
    const load = async () => {
      try {
        const uid = await main.addrToId(account);
        if (uid.eq(0)) {
          if (mountedRef.current) { setData({ registered: false }); setLoading(false); }
          return;
        }
        const [basic, ext, pendingROI, incomes] = await Promise.all([
          main.getUserBasic(uid),
          main.getUserExt(uid),
          views.calcPendingROI(uid).catch(() => ethers.BigNumber.from(0)),
          views.getUserIncome(uid).catch(() => [0,0,0,0,0,0])
        ]);
        if (!mountedRef.current) return;
        setData({
          registered: true,
          uid: uid.toString(),
          wallet: basic[0],
          totalInv: basic[1],
          totalEarned: basic[2],
          totalClaimed: basic[3],
          pending: basic[4],
          highestPkg: basic[5],
          directRef: ext[0],
          teamVol: ext[1],
          directRefBiz: ext[2],
          active: ext[3],
          blacklisted: ext[4],
          boosterElig: ext[5],
          pendingROI,
          roiInc: incomes[0],
          lvlInc: incomes[1],
          boostInc: incomes[2]
        });
        setLoading(false);
      } catch (err) {
        if (mountedRef.current) { setLoading(false); }
      }
    };
    setLoading(true);
    load();
    return () => { mountedRef.current = false; };
  }, [account, main, views, refreshFlag]);

  if (loading) return <InlineLoader text="Loading dashboard..." />;
  if (!account) return <div className="empty-state">Connect your wallet to view your dashboard</div>;
  if (!data?.registered) return (
    <div className="empty-state">
      <div className="empty-icon">◇</div>
      <div>You are not registered yet. Go to Invest to get started.</div>
    </div>
  );

  return (
    <>
      <div className="grid grid-4 mb-4">
        <StatCard label="Total Invested" value={fromBN(data.totalInv)} prefix="$" variant="primary" />
        <StatCard label="Total Earned" value={fromBN(data.totalEarned)} prefix="$" variant="success" />
        <StatCard label="Available Balance" value={fromBN(data.pending)} prefix="$" variant="warning" sub="Ready to withdraw" />
        <StatCard label="Total Claimed" value={fromBN(data.totalClaimed)} prefix="$" variant="primary" />
      </div>
      <div className="grid grid-4 mb-4">
        <StatCard label="ROI Earned" value={fromBN(data.roiInc)} prefix="$" variant="success" />
        <StatCard label="Level Earned" value={fromBN(data.lvlInc)} prefix="$" variant="primary" />
        <StatCard label="Pending ROI" value={fromBN(data.pendingROI)} prefix="$" variant="warning" sub="Not yet claimed" />
        <StatCard label="Direct Referrals" value={Number(data.directRef)} decimals={0} variant="purple" />
      </div>
      <div className="grid grid-4 mb-4">
        <StatCard label="Team Volume" value={fromBN(data.teamVol)} prefix="$" variant="primary" />
        <StatCard label="Direct Ref Biz" value={fromBN(data.directRefBiz)} prefix="$" variant="success" />
        <StatCard label="Highest Package" value={fromBN(data.highestPkg)} prefix="$" variant="warning" />
        <div className={`stat-card ${data.active && !data.blacklisted ? 'success' : 'danger'}`}>
          <div className="stat-label">Account Status</div>
          <div className="stat-value" style={{ fontSize: 18 }}>
            {data.blacklisted ? 'BLACKLISTED' : (data.active ? 'ACTIVE' : 'INACTIVE')}
          </div>
          <div className="stat-sub">
            ID: {data.uid} · {shortAddr(data.wallet)}
          </div>
        </div>
      </div>
    </>
  );
}
